import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import AppShell from '../components/AppShell';
import { tierDisplayName } from '../utils/tierNames';
import {
  Users, TrendingUp, Video, Image, Music, Zap, Activity,
  AlertTriangle, CheckCircle, XCircle, Clock, RefreshCw,
  Shield, DollarSign, Server, Database, HardDrive, Wifi,
  Loader2, Ban, RotateCcw, Search, UserX, UserCheck,
  Wrench, Trash2, Eye, Play, Mail, Download,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const REFRESH_INTERVAL = 15000;

export default function AdminDashboard() {
  const { user, token, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [cancelConfirm, setCancelConfirm] = useState(null);
  const [userSearch, setUserSearch] = useState('');
  const [userResults, setUserResults] = useState(null);
  const [userSearching, setUserSearching] = useState(false);
  const [suspendConfirm, setSuspendConfirm] = useState(null);

  // Maintenance state
  const [maintLoading, setMaintLoading] = useState({});
  const [cleanupDryRun, setCleanupDryRun] = useState(null);
  const [cleanupResult, setCleanupResult] = useState(null);
  const [orphanResult, setOrphanResult] = useState(null);
  const [thumbResult, setThumbResult] = useState(null);
  const [smokeResult, setSmokeResult] = useState(null);
  const [cleanupConfirm, setCleanupConfirm] = useState(false);
  const intervalRef = useRef(null);

  const fetchData = useCallback(async () => {
    if (!token) return;
    try {
      const r = await fetch(`${API}/api/admin/summary`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (r.status === 403) {
        toast.error('Access denied — founder account required');
        navigate('/dashboard');
        return;
      }
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      setData(d);
      setError(null);
      setLastRefresh(new Date());
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [token, navigate]);

  useEffect(() => {
    if (authLoading) return;
    if (!user || !user.is_founder) {
      toast.error('Access denied — founder account required');
      navigate('/dashboard');
      return;
    }
    fetchData();
    intervalRef.current = setInterval(fetchData, REFRESH_INTERVAL);
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [user, authLoading, fetchData, navigate]);

  const retryJob = async (jobId) => {
    // Optimistic: update UI immediately
    setData(prev => {
      if (!prev) return prev;
      const updated = { ...prev };
      const qm = { ...updated.queue_monitor };
      qm.recent_failed = (qm.recent_failed || []).filter(j => j.id !== jobId);
      qm.queued = (qm.queued || 0) + 1;
      qm.failed = Math.max(0, (qm.failed || 0) - 1);
      qm.total_in_queue = (qm.queued || 0) + (qm.rendering || 0);
      updated.queue_monitor = qm;
      return updated;
    });
    try {
      const r = await fetch(`${API}/api/admin/jobs/${jobId}/retry`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` },
      });
      const d = await r.json();
      if (d.status === 'already_processing') toast.info('Job is already processing');
      else if (d.status === 'already_completed') toast.info('Job already completed');
      else toast.success('Job queued for retry');
    } catch {
      toast.error('Failed to retry job');
    }
    fetchData();
  };

  const confirmCancel = (jobId) => setCancelConfirm(jobId);

  const executeCancel = async () => {
    const jobId = cancelConfirm;
    setCancelConfirm(null);
    // Optimistic
    setData(prev => {
      if (!prev) return prev;
      const updated = { ...prev };
      const qm = { ...updated.queue_monitor };
      qm.recent_failed = (qm.recent_failed || []).filter(j => j.id !== jobId);
      qm.queued = Math.max(0, (qm.queued || 0) - 1);
      qm.total_in_queue = Math.max(0, (qm.total_in_queue || 0) - 1);
      updated.queue_monitor = qm;
      return updated;
    });
    try {
      const r = await fetch(`${API}/api/admin/jobs/${jobId}/cancel`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) {
        const d = await r.json();
        toast.error(d.detail || 'Cannot cancel this job');
      } else {
        const d = await r.json();
        if (d.status === 'already_cancelled') toast.info('Job was already cancelled');
        else toast.success('Job cancelled');
      }
    } catch {
      toast.error('Failed to cancel job');
    }
    fetchData();
  };

  const searchUsers = async (q) => {
    if (!q || q.length < 2) { setUserResults(null); return; }
    setUserSearching(true);
    try {
      const r = await fetch(`${API}/api/admin/users/search?q=${encodeURIComponent(q)}&limit=10`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const d = await r.json();
      setUserResults(d);
    } catch { toast.error('Search failed'); }
    setUserSearching(false);
  };

  const executeSuspend = async () => {
    const uid = suspendConfirm;
    setSuspendConfirm(null);
    try {
      const r = await fetch(`${API}/api/admin/users/${uid}/suspend`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` },
      });
      const d = await r.json();
      if (!r.ok) { toast.error(d.detail || 'Action failed'); return; }
      toast.success(d.status === 'suspended' ? 'User suspended' : 'User unsuspended');
      searchUsers(userSearch);
    } catch { toast.error('Failed to update user'); }
  };

  // Maintenance actions
  const runMaintAction = async (action, setter) => {
    setMaintLoading(p => ({ ...p, [action]: true }));
    try {
      const r = await fetch(`${API}/api/admin/maintenance/${action}`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` },
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Action failed');
      setter(d);
      toast.success(`${action.replace(/-/g, ' ')} completed`);
    } catch (e) {
      toast.error(e.message || 'Action failed');
    }
    setMaintLoading(p => ({ ...p, [action]: false }));
  };

  const runCleanupDryRun = () => runMaintAction('cleanup-accounts/dry-run', setCleanupDryRun);
  const runCleanupExecute = async () => {
    setCleanupConfirm(false);
    setMaintLoading(p => ({ ...p, execute: true }));
    try {
      const r = await fetch(`${API}/api/admin/maintenance/cleanup-accounts/execute`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` },
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Cleanup failed');
      setCleanupResult(d);
      setCleanupDryRun(null);
      toast.success(`Cleaned ${d.accounts_deleted} accounts`);
    } catch (e) { toast.error(e.message); }
    setMaintLoading(p => ({ ...p, execute: false }));
  };
  const runOrphanScan = () => runMaintAction('orphan-scan', setOrphanResult);
  const runThumbRegen = () => runMaintAction('regenerate-thumbnails', setThumbResult);
  const runSmokeTest = () => runMaintAction('smoke-test', setSmokeResult);

  if (authLoading || loading) return <SkeletonDashboard />;
  if (error) return (
    <AppShell>
      <div className="flex items-center justify-center py-20">
        <div className="text-center space-y-3">
          <AlertTriangle className="w-8 h-8 text-red-400 mx-auto" />
          <p className="text-zinc-300">{error}</p>
          <button onClick={fetchData} className="text-amber-400 text-sm hover:underline" data-testid="error-retry-btn">Retry</button>
        </div>
      </div>
    </AppShell>
  );
  if (!data) return null;

  const { user_metrics: um, revenue: rev, generation_metrics: gm, system_health: sh, queue_monitor: qm, compliance: comp, errors: errs, activity_feed: af } = data;

  return (
    <AppShell>
      <div className="text-zinc-100" data-testid="admin-dashboard">
      {/* Cancel Confirm Modal */}
      {cancelConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" data-testid="cancel-confirm-modal">
          <div className="bg-zinc-900 border border-zinc-700 rounded-xl p-5 max-w-sm w-full mx-4 shadow-2xl">
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle className="w-5 h-5 text-red-400" />
              <h3 className="text-sm font-semibold text-zinc-100">Cancel this job?</h3>
            </div>
            <p className="text-xs text-zinc-400 mb-4">This cannot be undone. The job will be permanently cancelled and cannot be resumed.</p>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setCancelConfirm(null)} className="px-3 py-1.5 text-xs rounded-lg bg-zinc-800 text-zinc-300 hover:bg-zinc-700 transition-colors" data-testid="cancel-modal-dismiss">Keep Job</button>
              <button onClick={executeCancel} className="px-3 py-1.5 text-xs rounded-lg bg-red-600 text-white hover:bg-red-500 transition-colors" data-testid="cancel-modal-confirm">Cancel Job</button>
            </div>
          </div>
        </div>
      )}

      {/* Suspend Confirm Modal */}
      {suspendConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" data-testid="suspend-confirm-modal">
          <div className="bg-zinc-900 border border-zinc-700 rounded-xl p-5 max-w-sm w-full mx-4 shadow-2xl">
            <div className="flex items-center gap-2 mb-3">
              <UserX className="w-5 h-5 text-red-400" />
              <h3 className="text-sm font-semibold text-zinc-100">Toggle user suspension?</h3>
            </div>
            <p className="text-xs text-zinc-400 mb-4">This will suspend or unsuspend the user's account. Suspended users cannot log in.</p>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setSuspendConfirm(null)} className="px-3 py-1.5 text-xs rounded-lg bg-zinc-800 text-zinc-300 hover:bg-zinc-700 transition-colors" data-testid="suspend-modal-dismiss">Cancel</button>
              <button onClick={executeSuspend} className="px-3 py-1.5 text-xs rounded-lg bg-red-600 text-white hover:bg-red-500 transition-colors" data-testid="suspend-modal-confirm">Confirm</button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <header className="border-b border-white/[0.04] bg-background/80 backdrop-blur-sm sticky top-0 z-20 px-4 sm:px-6 py-3">
        <div className="flex items-center justify-between max-w-[1440px] mx-auto">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-primary/[0.08] flex items-center justify-center">
              <Shield className="w-4 h-4 text-primary" />
            </div>
            <div>
              <p className="text-[9px] text-primary/60 uppercase tracking-[0.15em] font-semibold">Admin Console</p>
              <h1 className="text-sm font-semibold text-zinc-100">System Administration</h1>
            </div>
          </div>
          <div className="flex items-center gap-3 text-[10px] text-zinc-500">
            {lastRefresh && <span data-testid="last-refresh">Updated {lastRefresh.toLocaleTimeString()}</span>}
            <button onClick={fetchData} className="p-1.5 rounded-md hover:bg-zinc-800 transition-colors" data-testid="refresh-btn">
              <RefreshCw className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1440px] mx-auto px-4 sm:px-6 py-5 space-y-5">
        {/* Top Row: Critical Metrics */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="top-metrics">
          <MetricCard label="Active Users (24h)" value={um.active_24h} icon={Users} color="blue" />
          <MetricCard label="New Signups Today" value={um.new_today} icon={TrendingUp} color="emerald" />
          <MetricCard label="Content Today" value={gm.total_content_today} icon={Zap} color="amber" />
          <MetricCard label="Monthly Revenue" value={`$${rev.mrr}`} icon={DollarSign} color="emerald" />
          <MetricCard label="Credits Used" value={data.credits_used_today} icon={Activity} color="violet" />
          <MetricCard label="Jobs in Queue" value={qm.total_in_queue} icon={Clock} color={qm.total_in_queue > 10 ? 'red' : 'zinc'} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
          {/* User Metrics */}
          <DashCard title="User Metrics" icon={Users} testId="user-metrics">
            <StatRow label="Total Users" value={um.total_users} />
            <StatRow label="Active (24h)" value={um.active_24h} />
            <StatRow label="Active (7d)" value={um.active_7d} />
            <StatRow label="New Today" value={um.new_today} highlight />
            <div className="border-t border-zinc-800/50 pt-2 mt-2" />
            <StatRow label="Pro / Elite" value={um.pro_elite_count} />
            <StatRow label="Standard" value={um.standard_count} />
            <StatRow label="Free Trial" value={um.trial_count} />
            <StatRow label="Conversion Rate" value={`${um.conversion_rate}%`} highlight />
          </DashCard>

          {/* Revenue & Billing */}
          <DashCard title="Revenue & Billing" icon={DollarSign} testId="revenue-metrics">
            <StatRow label="MRR" value={`$${rev.mrr}`} highlight />
            <StatRow label="Today's Revenue" value={`$${rev.today_revenue}`} />
            <StatRow label="Failed Payments" value={rev.failed_payments} warn={rev.failed_payments > 0} />
            <StatRow label="Credit Purchases (today)" value={rev.credit_purchases_today} />
            <StatRow label="Credit Purchases (month)" value={rev.credit_purchases_month} />
            <div className="border-t border-zinc-800/50 pt-2 mt-2">
              <p className="text-[10px] text-zinc-600 mb-1.5">Active Subscriptions</p>
              {Object.entries(rev.active_subscriptions || {}).length > 0
                ? Object.entries(rev.active_subscriptions).map(([t, c]) => (
                    <StatRow key={t} label={t.charAt(0).toUpperCase() + t.slice(1)} value={c} />
                  ))
                : <p className="text-[10px] text-zinc-600 italic">No active Stripe subscriptions</p>
              }
            </div>
          </DashCard>

          {/* Content Generation */}
          <DashCard title="Content Generation" icon={Video} testId="generation-metrics">
            <StatRow label="Videos Today" value={gm.videos_today} icon={Video} />
            <StatRow label="Images Today" value={gm.images_today} icon={Image} />
            <StatRow label="Music Today" value={gm.music_today} icon={Music} />
            <StatRow label="AI Influencers" value={gm.influencers_total} />
            <div className="border-t border-zinc-800/50 pt-2 mt-2">
              <p className="text-[10px] text-zinc-600 mb-2">7-Day Trend</p>
              <TrendBars data={gm.trend_7d} />
            </div>
          </DashCard>

          {/* System Health */}
          <DashCard title="System Health" icon={Server} testId="system-health">
            <ServiceRow label="API" status={sh.services.api} icon={Server} />
            <ServiceRow label="Database" status={sh.services.database} icon={Database} />
            <ServiceRow label="R2 Storage" status={sh.services.r2_storage} icon={HardDrive} />
            <ServiceRow label="Job Queue" status={sh.services.job_queue} icon={Clock} />
            <ServiceRow label="Social Publishing" status={sh.services.social_publishing === 'offline' ? 'Coming next' : sh.services.social_publishing} icon={Wifi} />
            <div className="border-t border-zinc-800/50 pt-2 mt-2">
              <p className="text-[10px] text-zinc-600 mb-1.5">Performance</p>
              <StatRow label="Avg Render Time" value={`${Math.round(sh.performance.avg_video_render_time)}s`} />
              <StatRow label="Failed Job Rate" value={`${sh.performance.failed_job_rate}%`} warn={sh.performance.failed_job_rate > 10} />
            </div>
          </DashCard>

          {/* Job Queue Monitor */}
          <DashCard title="Job Queue Monitor" icon={Activity} testId="queue-monitor">
            <div className="grid grid-cols-3 gap-2 mb-3">
              <MiniStat label="Rendering" value={qm.rendering} color="blue" />
              <MiniStat label="Queued" value={qm.queued} color="amber" />
              <MiniStat label="Failed" value={qm.failed} color="red" />
            </div>
            <StatRow label="Stuck Jobs (>2h)" value={qm.stuck} warn={qm.stuck > 0} />
            {qm.recent_failed?.length > 0 && (
              <div className="border-t border-zinc-800/50 pt-2 mt-2 space-y-1.5">
                <p className="text-[10px] text-zinc-600">Recent Failed</p>
                {qm.recent_failed.slice(0, 5).map(j => (
                  <div key={j.id} className="flex items-start gap-1.5 text-[10px] bg-zinc-900/60 rounded-md px-2 py-1.5">
                    <XCircle className="w-3 h-3 text-red-400 shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <p className="text-zinc-400 truncate">{j.render_message?.slice(0, 60) || 'Unknown error'}</p>
                      <p className="text-zinc-600">{j.id.slice(0, 8)}</p>
                    </div>
                    <div className="flex gap-1 shrink-0">
                      <button onClick={() => retryJob(j.id)} className="p-1 rounded hover:bg-zinc-800 text-amber-400" title="Retry" data-testid={`retry-${j.id.slice(0,8)}`}>
                        <RotateCcw className="w-3 h-3" />
                      </button>
                      <button onClick={() => confirmCancel(j.id)} className="p-1 rounded hover:bg-zinc-800 text-red-400" title="Cancel" data-testid={`cancel-${j.id.slice(0,8)}`}>
                        <Ban className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </DashCard>

          {/* Compliance */}
          <DashCard title="Compliance" icon={Shield} testId="compliance-metrics">
            <StatRow label="NSFW-Enabled Users" value={comp.nsfw_enabled_users} />
            <StatRow label="Blocked Attempts (24h)" value={comp.blocked_attempts} warn={comp.blocked_attempts > 0} />
            <StatRow label="Policy Violations (24h)" value={comp.policy_violations} warn={comp.policy_violations > 0} />
          </DashCard>
        </div>

        {/* User Management */}
        <div className="bg-zinc-900/50 border border-zinc-800/40 rounded-xl p-4" data-testid="user-management">
          <div className="flex items-center gap-2 mb-3">
            <Users className="w-4 h-4 text-zinc-500" />
            <h2 className="text-xs font-semibold text-zinc-300 uppercase tracking-wider">User Management</h2>
          </div>
          <div className="flex gap-2 mb-3">
            <div className="relative flex-1">
              <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-500" />
              <input
                type="text"
                value={userSearch}
                onChange={(e) => { setUserSearch(e.target.value); searchUsers(e.target.value); }}
                placeholder="Search by email or name..."
                className="w-full bg-zinc-800/50 border border-zinc-700/50 rounded-lg pl-8 pr-3 py-1.5 text-xs text-zinc-200 placeholder:text-zinc-600 focus:outline-none focus:border-zinc-600"
                data-testid="user-search-input"
              />
            </div>
            {userSearching && <Loader2 className="w-4 h-4 animate-spin text-zinc-500 shrink-0 mt-1" />}
          </div>
          {userResults && (
            <div className="space-y-1 max-h-[220px] overflow-y-auto">
              <p className="text-[10px] text-zinc-600 mb-1">{userResults.total} result{userResults.total !== 1 ? 's' : ''}</p>
              {userResults.users?.map(u => (
                <div key={u.id} className="flex items-center gap-2 text-[10px] bg-zinc-900/60 rounded-md px-2.5 py-1.5">
                  <div className="w-5 h-5 rounded-full bg-zinc-700 flex items-center justify-center text-[9px] font-medium text-zinc-300 shrink-0">
                    {u.name?.charAt(0)?.toUpperCase() || '?'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-zinc-300 truncate">{u.email}</p>
                    <div className="flex gap-2 text-zinc-600">
                      <span>{tierDisplayName(u.tier)}</span>
                      <span>{u.monthly_credits ?? 0} credits</span>
                      {u.nsfw_enabled && <span className="text-red-400">NSFW</span>}
                      {u.suspended && <span className="text-red-400 font-medium">SUSPENDED</span>}
                    </div>
                  </div>
                  {!u.is_founder && (
                    <button
                      onClick={() => setSuspendConfirm(u.id)}
                      className={`p-1 rounded text-[9px] ${u.suspended ? 'hover:bg-emerald-900/30 text-emerald-400' : 'hover:bg-red-900/30 text-red-400'}`}
                      title={u.suspended ? 'Unsuspend' : 'Suspend'}
                      data-testid={`suspend-${u.id.slice(0,8)}`}
                    >
                      {u.suspended ? <UserCheck className="w-3.5 h-3.5" /> : <UserX className="w-3.5 h-3.5" />}
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
          {!userResults && <p className="text-[10px] text-zinc-600 text-center py-2">Type at least 2 characters to search</p>}
        </div>

        {/* Lead Capture */}
        <LeadsSection token={token} />

        {/* Affiliate Payouts */}
        <AffiliatePayoutsSection token={token} />

        {/* Bottom Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Error Monitor */}
          <DashCard title="Error Monitor" icon={AlertTriangle} testId="error-monitor" wide>
            {errs?.length > 0 ? (
              <div className="space-y-1.5 max-h-[260px] overflow-y-auto pr-1">
                {errs.map((e, i) => (
                  <div key={i} className="flex items-start gap-2 text-[10px] bg-zinc-900/50 rounded-md px-2.5 py-1.5">
                    <SeverityDot type={e.type} />
                    <div className="flex-1 min-w-0">
                      <p className="text-zinc-300 truncate">{e.message || e.type}</p>
                      <p className="text-zinc-600">{e.timestamp ? new Date(e.timestamp).toLocaleString() : ''}</p>
                    </div>
                    <span className="text-[9px] px-1.5 py-0.5 rounded bg-zinc-800 text-zinc-500 shrink-0">{e.type}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-[11px] text-zinc-600 text-center py-6">No recent errors</p>
            )}
          </DashCard>

          {/* Activity Feed */}
          <DashCard title="Activity Feed" icon={Activity} testId="activity-feed" wide>
            {af?.length > 0 ? (
              <div className="space-y-1.5 max-h-[260px] overflow-y-auto pr-1">
                {af.map((a, i) => (
                  <div key={i} className="flex items-center gap-2 text-[10px] bg-zinc-900/50 rounded-md px-2.5 py-1.5">
                    <ActivityIcon type={a.type} />
                    <div className="flex-1 min-w-0">
                      <p className="text-zinc-300 truncate">
                        {a.type === 'signup' && `New user: ${a.email}`}
                        {a.type === 'video_generated' && `Video: ${a.title || a.id?.slice(0,8)} (${a.platform})`}
                        {a.type === 'image_generated' && `Image: ${a.prompt || a.id?.slice(0,8)}`}
                      </p>
                      <p className="text-zinc-600">{a.at ? timeAgo(a.at) : ''}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-[11px] text-zinc-600 text-center py-6">No recent activity</p>
            )}
          </DashCard>
        </div>

        {/* Maintenance Section */}
        <div className="mt-4 border-t border-zinc-800/50 pt-4" data-testid="maintenance-section">
          <div className="flex items-center gap-2 mb-4">
            <Wrench className="w-4 h-4 text-zinc-400" />
            <h2 className="text-sm font-semibold text-zinc-200">Maintenance</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {/* Cleanup Dev/Test Accounts */}
            <div className="bg-zinc-900/60 border border-zinc-800/40 rounded-xl p-3" data-testid="maint-cleanup-accounts">
              <div className="flex items-center gap-2 mb-2">
                <Trash2 className="w-3.5 h-3.5 text-red-400" />
                <h3 className="text-xs font-medium text-zinc-200">Cleanup Dev/Test Accounts</h3>
              </div>
              <p className="text-[10px] text-zinc-500 mb-3">Remove test accounts, emails with "test", and stale trial users (&gt;24h). Deletes all related media and DB records.</p>
              <button onClick={runCleanupDryRun} disabled={maintLoading['cleanup-accounts/dry-run']} className="w-full px-2.5 py-1.5 text-[10px] font-medium rounded-lg bg-zinc-800 text-zinc-300 hover:bg-zinc-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-1" data-testid="cleanup-dry-run-btn">
                {maintLoading['cleanup-accounts/dry-run'] ? <Loader2 className="w-3 h-3 animate-spin" /> : <Eye className="w-3 h-3" />}
                Dry Run (Preview)
              </button>
              {cleanupDryRun && (
                <div className="mt-2 p-2 bg-zinc-950 rounded-lg text-[10px]" data-testid="cleanup-dry-run-result">
                  <p className="text-zinc-300 font-medium">{cleanupDryRun.accounts_matched} accounts matched</p>
                  {cleanupDryRun.related_records && Object.entries(cleanupDryRun.related_records).map(([k, v]) => (
                    <p key={k} className="text-zinc-500">{k}: {v} records</p>
                  ))}
                  {cleanupDryRun.accounts?.slice(0, 5).map(a => (
                    <p key={a.id} className="text-zinc-600 truncate">{a.email}</p>
                  ))}
                  {cleanupDryRun.accounts_matched > 0 && (
                    <button onClick={() => setCleanupConfirm(true)} className="mt-1.5 w-full px-2 py-1 text-[10px] font-medium rounded bg-red-600/20 text-red-400 hover:bg-red-600/30 transition-colors" data-testid="cleanup-execute-btn">
                      Execute Cleanup
                    </button>
                  )}
                </div>
              )}
              {cleanupResult && (
                <div className="mt-2 p-2 bg-emerald-950/20 border border-emerald-500/10 rounded-lg text-[10px]" data-testid="cleanup-execute-result">
                  <p className="text-emerald-400 font-medium">Deleted {cleanupResult.accounts_deleted} accounts</p>
                  <p className="text-zinc-500">R2 objects: {cleanupResult.r2_objects_deleted}</p>
                </div>
              )}
            </div>

            {/* Orphan Cleanup */}
            <div className="bg-zinc-900/60 border border-zinc-800/40 rounded-xl p-3" data-testid="maint-orphan-scan">
              <div className="flex items-center gap-2 mb-2">
                <Database className="w-3.5 h-3.5 text-amber-400" />
                <h3 className="text-xs font-medium text-zinc-200">Orphan Cleanup Now</h3>
              </div>
              <p className="text-[10px] text-zinc-500 mb-3">Scan DB for items whose R2/local file is missing. Marks them hidden=true and logs to admin audit.</p>
              <button onClick={runOrphanScan} disabled={maintLoading['orphan-scan']} className="w-full px-2.5 py-1.5 text-[10px] font-medium rounded-lg bg-zinc-800 text-zinc-300 hover:bg-zinc-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-1" data-testid="orphan-scan-btn">
                {maintLoading['orphan-scan'] ? <Loader2 className="w-3 h-3 animate-spin" /> : <Search className="w-3 h-3" />}
                Run Orphan Scan
              </button>
              {orphanResult && (
                <div className="mt-2 p-2 bg-zinc-950 rounded-lg text-[10px]" data-testid="orphan-scan-result">
                  <p className={`font-medium ${orphanResult.total > 0 ? 'text-amber-400' : 'text-emerald-400'}`}>
                    {orphanResult.total > 0 ? `Flagged ${orphanResult.total} orphans` : 'No orphans found'}
                  </p>
                  {orphanResult.flagged && Object.entries(orphanResult.flagged).map(([k, v]) => (
                    <p key={k} className="text-zinc-500">{k}: {v}</p>
                  ))}
                  <p className="text-zinc-600">R2: {orphanResult.r2_available ? 'Connected' : 'Local mode'}</p>
                </div>
              )}
            </div>

            {/* Regenerate Thumbnails */}
            <div className="bg-zinc-900/60 border border-zinc-800/40 rounded-xl p-3" data-testid="maint-thumb-regen">
              <div className="flex items-center gap-2 mb-2">
                <Image className="w-3.5 h-3.5 text-blue-400" />
                <h3 className="text-xs font-medium text-zinc-200">Regenerate Thumbnails</h3>
              </div>
              <p className="text-[10px] text-zinc-500 mb-3">Rebuild thumbnails for videos missing thumbnail_url. Extracts frame from video, stores to R2 or local.</p>
              <button onClick={runThumbRegen} disabled={maintLoading['regenerate-thumbnails']} className="w-full px-2.5 py-1.5 text-[10px] font-medium rounded-lg bg-zinc-800 text-zinc-300 hover:bg-zinc-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-1" data-testid="thumb-regen-btn">
                {maintLoading['regenerate-thumbnails'] ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                Regenerate Thumbnails
              </button>
              {thumbResult && (
                <div className="mt-2 p-2 bg-zinc-950 rounded-lg text-[10px]" data-testid="thumb-regen-result">
                  <p className={`font-medium ${thumbResult.repaired > 0 ? 'text-emerald-400' : 'text-zinc-400'}`}>
                    {thumbResult.repaired}/{thumbResult.total_missing} repaired
                  </p>
                  {thumbResult.failed > 0 && <p className="text-red-400">Failed: {thumbResult.failed}</p>}
                </div>
              )}
            </div>

            {/* Smoke Test Suite */}
            <div className="bg-zinc-900/60 border border-zinc-800/40 rounded-xl p-3" data-testid="maint-smoke-test">
              <div className="flex items-center gap-2 mb-2">
                <Play className="w-3.5 h-3.5 text-emerald-400" />
                <h3 className="text-xs font-medium text-zinc-200">Run Smoke Test Suite</h3>
              </div>
              <p className="text-[10px] text-zinc-500 mb-3">Lightweight checks: DB, Stripe, R2, credits, UGC mode, affiliate kit. Returns PASS/FAIL report.</p>
              <button onClick={runSmokeTest} disabled={maintLoading['smoke-test']} className="w-full px-2.5 py-1.5 text-[10px] font-medium rounded-lg bg-emerald-600/20 text-emerald-400 hover:bg-emerald-600/30 transition-colors disabled:opacity-50 flex items-center justify-center gap-1" data-testid="smoke-test-btn">
                {maintLoading['smoke-test'] ? <Loader2 className="w-3 h-3 animate-spin" /> : <Zap className="w-3 h-3" />}
                Run Smoke Test
              </button>
              {smokeResult && (
                <div className="mt-2 p-2 bg-zinc-950 rounded-lg text-[10px] space-y-1" data-testid="smoke-test-result">
                  <div className="flex items-center justify-between">
                    <span className={`font-bold ${smokeResult.overall === 'PASS' ? 'text-emerald-400' : smokeResult.overall === 'PARTIAL' ? 'text-amber-400' : 'text-red-400'}`}>
                      {smokeResult.overall}
                    </span>
                    <span className="text-zinc-500">{smokeResult.passed}/{smokeResult.total}</span>
                  </div>
                  {smokeResult.results?.map((r, i) => (
                    <div key={i} className="flex items-center gap-1.5">
                      {r.status === 'PASS' ? <CheckCircle className="w-2.5 h-2.5 text-emerald-400 shrink-0" /> :
                       r.status === 'WARN' ? <AlertTriangle className="w-2.5 h-2.5 text-amber-400 shrink-0" /> :
                       <XCircle className="w-2.5 h-2.5 text-red-400 shrink-0" />}
                      <span className="text-zinc-400 truncate">{r.check}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Cleanup Confirm Modal */}
        {cleanupConfirm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" data-testid="cleanup-confirm-modal">
            <div className="bg-zinc-900 border border-zinc-700 rounded-xl p-5 max-w-sm w-full mx-4 shadow-2xl">
              <div className="flex items-center gap-2 mb-3">
                <AlertTriangle className="w-5 h-5 text-red-400" />
                <h3 className="text-sm font-semibold text-zinc-100">Confirm Account Cleanup</h3>
              </div>
              <p className="text-xs text-zinc-400 mb-2">This will permanently delete <span className="text-red-400 font-medium">{cleanupDryRun?.accounts_matched} accounts</span> and all their associated data including:</p>
              <ul className="text-[10px] text-zinc-500 mb-4 space-y-0.5 ml-3">
                <li>All videos, images, and music</li>
                <li>R2 storage objects</li>
                <li>Conversations, referrals, ledger entries</li>
              </ul>
              <p className="text-[10px] text-red-400 mb-4">This cannot be undone.</p>
              <div className="flex gap-2 justify-end">
                <button onClick={() => setCleanupConfirm(false)} className="px-3 py-1.5 text-xs rounded-lg bg-zinc-800 text-zinc-300 hover:bg-zinc-700 transition-colors" data-testid="cleanup-modal-cancel">Cancel</button>
                <button onClick={runCleanupExecute} disabled={maintLoading.execute} className="px-3 py-1.5 text-xs rounded-lg bg-red-600 text-white hover:bg-red-500 transition-colors disabled:opacity-50 flex items-center gap-1" data-testid="cleanup-modal-confirm">
                  {maintLoading.execute ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
                  Delete Permanently
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
    </AppShell>
  );
}

/* Sub-components */

function MetricCard({ label, value, icon: Icon, color = 'zinc' }) {
  const colors = {
    blue: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
    emerald: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
    amber: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
    violet: 'bg-violet-500/10 text-violet-400 border-violet-500/20',
    red: 'bg-red-500/10 text-red-400 border-red-500/20',
    zinc: 'bg-zinc-800/50 text-zinc-300 border-zinc-700/30',
  };
  return (
    <div className={`rounded-xl border px-3 py-3 ${colors[color]} transition-all hover:scale-[1.02]`} data-testid={`metric-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="flex items-center gap-1.5 mb-1.5">
        <Icon className="w-3.5 h-3.5 opacity-70" />
        <span className="text-[9px] opacity-70 uppercase tracking-wider font-medium">{label}</span>
      </div>
      <p className="text-xl font-bold tabular-nums">{value}</p>
    </div>
  );
}

function DashCard({ title, icon: Icon, children, testId }) {
  return (
    <div className="bg-zinc-900/50 border border-zinc-800/40 rounded-xl p-4" data-testid={testId}>
      <div className="flex items-center gap-2 mb-3">
        <Icon className="w-4 h-4 text-zinc-500" />
        <h2 className="text-xs font-semibold text-zinc-300 uppercase tracking-wider">{title}</h2>
      </div>
      {children}
    </div>
  );
}

function StatRow({ label, value, icon: Icon, highlight, warn }) {
  return (
    <div className="flex items-center justify-between py-0.5">
      <span className="text-[11px] text-zinc-500 flex items-center gap-1.5">
        {Icon && <Icon className="w-3 h-3" />}
        {label}
      </span>
      <span className={`text-[11px] font-medium tabular-nums ${warn ? 'text-red-400' : highlight ? 'text-amber-400' : 'text-zinc-300'}`}>{value}</span>
    </div>
  );
}

function ServiceRow({ label, status, icon: Icon }) {
  const s = status?.toLowerCase();
  const color = s === 'healthy' || s === 'connected' ? 'text-emerald-400' : s === 'degraded' || s === 'overloaded' ? 'text-amber-400' : s === 'coming next' ? 'text-zinc-500' : 'text-red-400';
  const dot = s === 'healthy' || s === 'connected' ? 'bg-emerald-400' : s === 'degraded' || s === 'overloaded' ? 'bg-amber-400' : s === 'coming next' ? 'bg-zinc-500' : 'bg-red-400';
  const pulse = s === 'coming next' ? '' : 'animate-pulse';
  return (
    <div className="flex items-center justify-between py-0.5">
      <span className="text-[11px] text-zinc-500 flex items-center gap-1.5"><Icon className="w-3 h-3" />{label}</span>
      <span className={`text-[10px] font-medium flex items-center gap-1.5 ${color}`}>
        <span className={`w-1.5 h-1.5 rounded-full ${dot} ${pulse}`} />
        {status}
      </span>
    </div>
  );
}

function MiniStat({ label, value, color }) {
  const bg = { blue: 'bg-blue-500/10 text-blue-400', amber: 'bg-amber-500/10 text-amber-400', red: 'bg-red-500/10 text-red-400' };
  return (
    <div className={`rounded-lg px-2 py-1.5 text-center ${bg[color]}`}>
      <p className="text-lg font-bold tabular-nums">{value}</p>
      <p className="text-[9px] opacity-70">{label}</p>
    </div>
  );
}

function TrendBars({ data }) {
  if (!data?.length) return null;
  const maxV = Math.max(...data.map(d => d.videos + d.images), 1);
  return (
    <div className="flex items-end gap-1 h-10">
      {data.map((d, i) => (
        <div key={i} className="flex-1 flex flex-col items-center gap-0.5">
          <div className="w-full flex flex-col-reverse gap-px">
            <div className="bg-blue-500/60 rounded-sm" style={{ height: `${Math.max(2, (d.videos / maxV) * 28)}px` }} title={`${d.videos} videos`} />
            <div className="bg-violet-500/60 rounded-sm" style={{ height: `${Math.max(0, (d.images / maxV) * 28)}px` }} title={`${d.images} images`} />
          </div>
          <span className="text-[8px] text-zinc-600">{d.date?.split('/')[1]}</span>
        </div>
      ))}
    </div>
  );
}

function LeadsSection({ token }) {
  const [leads, setLeads] = React.useState(null);
  const [stats, setStats] = React.useState(null);
  const [exporting, setExporting] = React.useState(false);

  React.useEffect(() => {
    if (!token) return;
    fetch(`${API}/api/admin/leads?limit=8`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => { setLeads(d.leads || []); setStats(d.stats || {}); })
      .catch(() => {});
  }, [token]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const r = await fetch(`${API}/api/admin/leads/export`, { headers: { Authorization: `Bearer ${token}` } });
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = 'delphi_leads.csv'; a.click();
      URL.revokeObjectURL(url);
      toast.success('Leads exported');
    } catch { toast.error('Export failed'); }
    setExporting(false);
  };

  return (
    <div className="bg-zinc-900/40 border border-zinc-800/40 rounded-xl p-4" data-testid="leads-section">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Mail className="w-4 h-4 text-emerald-400" />
          <h2 className="text-xs font-semibold text-zinc-300 uppercase tracking-wider">Lead Capture</h2>
        </div>
        <button
          onClick={handleExport}
          disabled={exporting}
          className="flex items-center gap-1.5 text-[10px] text-zinc-400 hover:text-white bg-zinc-800/60 hover:bg-zinc-700/60 px-2.5 py-1 rounded-md transition-colors"
          data-testid="leads-export-btn"
        >
          <Download className="w-3 h-3" />
          {exporting ? 'Exporting…' : 'Export CSV'}
        </button>
      </div>

      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
          <MiniStat value={stats.total_all ?? '—'} label="Total Leads" color="blue" />
          <MiniStat value={stats.last_24h ?? '—'} label="Last 24h" color="emerald" />
          <MiniStat value={stats.sent ?? '—'} label="Emails Sent" color="amber" />
          <MiniStat value={`${stats.success_rate ?? 0}%`} label="Delivery Rate" color={stats.success_rate > 80 ? 'emerald' : 'red'} />
        </div>
      )}

      {leads === null ? (
        <p className="text-[10px] text-zinc-600 text-center py-4">Loading…</p>
      ) : leads.length === 0 ? (
        <p className="text-[10px] text-zinc-600 text-center py-4">No leads captured yet</p>
      ) : (
        <div className="space-y-1 max-h-[220px] overflow-y-auto pr-1">
          {leads.map((l, i) => (
            <div key={l.id || i} className="flex items-center justify-between gap-2 text-[10px] bg-zinc-900/50 rounded-md px-2.5 py-1.5" data-testid={`lead-row-${i}`}>
              <div className="flex items-center gap-2 min-w-0">
                <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${l.status === 'sent' ? 'bg-emerald-400' : l.status === 'failed' ? 'bg-red-400' : 'bg-zinc-500'}`} />
                <span className="text-zinc-300 truncate">{l.email}</span>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <span className="text-zinc-600">{l.source || '—'}</span>
                <span className="text-zinc-600">{l.created_at ? timeAgo(l.created_at) : ''}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


function AffiliatePayoutsSection({ token }) {
  const [affiliates, setAffiliates] = React.useState([]);
  const [payoutRuns, setPayoutRuns] = React.useState([]);
  const [runningPayout, setRunningPayout] = React.useState(false);
  const [runningDryRun, setRunningDryRun] = React.useState(false);
  const [loadedAff, setLoadedAff] = React.useState(false);
  const [selectedRun, setSelectedRun] = React.useState(null);
  const [confirmAction, setConfirmAction] = React.useState(null); // 'real' | 'dry'
  const headers = { Authorization: `Bearer ${token}` };

  const load = React.useCallback(async () => {
    try {
      const [affRes, runsRes] = await Promise.all([
        fetch(`${API}/api/admin/affiliates`, { headers }).then(r => r.json()),
        fetch(`${API}/api/admin/affiliate-payouts`, { headers }).then(r => r.json()),
      ]);
      setAffiliates(affRes);
      setPayoutRuns(Array.isArray(runsRes) ? runsRes : []);
    } catch {}
    setLoadedAff(true);
  }, [token]);

  React.useEffect(() => { load(); }, [load]);

  const executeAction = async () => {
    const action = confirmAction;
    setConfirmAction(null);
    if (action === 'real') {
      setRunningPayout(true);
      try {
        await fetch(`${API}/api/admin/affiliate-payouts/run`, { method: 'POST', headers });
        toast.success('Payout run completed');
        load();
      } catch { toast.error('Payout run failed'); }
      setRunningPayout(false);
    } else if (action === 'dry') {
      setRunningDryRun(true);
      try {
        await fetch(`${API}/api/admin/affiliate-payouts/dry-run`, { method: 'POST', headers });
        toast.success('Dry-run completed');
        load();
      } catch { toast.error('Dry-run failed'); }
      setRunningDryRun(false);
    }
  };

  // Load full run details when clicking a row
  const openRunDetail = async (run) => {
    // If the run already has affiliate_details (from the list), use it
    if (run.affiliate_details) {
      setSelectedRun(run);
      return;
    }
    // Otherwise the list might not have full details — just show what we have
    setSelectedRun(run);
  };

  if (!loadedAff) return null;

  const statusColor = (s) => {
    if (s === 'completed') return 'text-emerald-400 bg-emerald-500/10';
    if (s === 'dry_run_completed') return 'text-blue-400 bg-blue-500/10';
    if (s === 'failed') return 'text-red-400 bg-red-500/10';
    if (s === 'running') return 'text-amber-400 bg-amber-500/10';
    return 'text-zinc-400 bg-zinc-800';
  };

  const actionColor = (a) => {
    if (a === 'paid') return 'text-emerald-400';
    if (a === 'dry_run_would_pay') return 'text-blue-400';
    if (a === 'error') return 'text-red-400';
    if (a?.includes('skipped')) return 'text-zinc-500';
    return 'text-zinc-400';
  };

  const actionLabel = (a) => {
    if (a === 'paid') return 'Paid';
    if (a === 'dry_run_would_pay') return 'Would Pay';
    if (a === 'error') return 'Error';
    if (a === 'skipped_not_connected') return 'Not Connected';
    if (a === 'skipped_below_threshold') return 'Below Threshold';
    if (a === 'skipped_founder') return 'Founder';
    if (a === 'skipped_already_paid_this_run') return 'Already Paid';
    return a || '—';
  };

  return (
    <>
      {/* Confirm Modal */}
      {confirmAction && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" data-testid="payout-confirm-modal">
          <div className="bg-zinc-900 border border-zinc-700 rounded-xl p-5 max-w-sm w-full mx-4 shadow-2xl">
            <div className="flex items-center gap-2 mb-3">
              {confirmAction === 'real' ? (
                <AlertTriangle className="w-5 h-5 text-red-400" />
              ) : (
                <Activity className="w-5 h-5 text-blue-400" />
              )}
              <h3 className="text-sm font-semibold text-zinc-100">
                {confirmAction === 'real' ? 'Run Real Payout?' : 'Run Dry-Run Payout?'}
              </h3>
            </div>
            <p className="text-xs text-zinc-400 mb-4">
              {confirmAction === 'real'
                ? 'This will execute real Stripe transfers to all eligible connected affiliates above the $50 threshold. This action moves real money and cannot be undone.'
                : 'This will simulate a payout run without making any Stripe transfers. Safe to run anytime — no money will be moved.'}
            </p>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setConfirmAction(null)} className="px-3 py-1.5 text-xs rounded-lg bg-zinc-800 text-zinc-300 hover:bg-zinc-700 transition-colors" data-testid="payout-confirm-cancel">Cancel</button>
              <button
                onClick={executeAction}
                className={`px-3 py-1.5 text-xs rounded-lg text-white transition-colors ${confirmAction === 'real' ? 'bg-red-600 hover:bg-red-500' : 'bg-blue-600 hover:bg-blue-500'}`}
                data-testid="payout-confirm-execute"
              >
                {confirmAction === 'real' ? 'Execute Real Payout' : 'Run Dry-Run'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Run Detail Drawer */}
      {selectedRun && (
        <div className="fixed inset-0 z-50 flex justify-end" data-testid="payout-run-drawer">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setSelectedRun(null)} />
          <div className="relative w-full max-w-lg bg-zinc-950 border-l border-zinc-800 overflow-y-auto">
            <div className="sticky top-0 bg-zinc-950/95 backdrop-blur-sm border-b border-zinc-800 px-4 py-3 flex items-center justify-between z-10">
              <div>
                <h3 className="text-sm font-semibold text-zinc-100" data-testid="drawer-title">Payout Run Details</h3>
                <p className="text-[10px] text-zinc-500">{selectedRun.id?.slice(0, 12)}...</p>
              </div>
              <button onClick={() => setSelectedRun(null)} className="p-1.5 rounded-lg hover:bg-zinc-800 text-zinc-400" data-testid="drawer-close">
                <XCircle className="w-4 h-4" />
              </button>
            </div>

            <div className="p-4 space-y-4">
              {/* Run Summary */}
              <div className="grid grid-cols-2 gap-2" data-testid="drawer-summary">
                <div className="bg-zinc-900/50 rounded-lg px-3 py-2">
                  <p className="text-[9px] text-zinc-600 uppercase">Status</p>
                  <p className={`text-xs font-medium ${statusColor(selectedRun.status).split(' ')[0]}`}>
                    {selectedRun.status?.replace(/_/g, ' ')}
                  </p>
                </div>
                <div className="bg-zinc-900/50 rounded-lg px-3 py-2">
                  <p className="text-[9px] text-zinc-600 uppercase">Run At</p>
                  <p className="text-xs text-zinc-300">{selectedRun.run_at ? new Date(selectedRun.run_at).toLocaleString() : '—'}</p>
                </div>
                <div className="bg-zinc-900/50 rounded-lg px-3 py-2">
                  <p className="text-[9px] text-zinc-600 uppercase">Total Paid</p>
                  <p className="text-xs font-medium text-emerald-400">${((selectedRun.totals?.total_cents || 0) / 100).toFixed(2)}</p>
                </div>
                <div className="bg-zinc-900/50 rounded-lg px-3 py-2">
                  <p className="text-[9px] text-zinc-600 uppercase">Completed At</p>
                  <p className="text-xs text-zinc-300">{selectedRun.completed_at ? new Date(selectedRun.completed_at).toLocaleString() : '—'}</p>
                </div>
              </div>

              {/* Totals Breakdown */}
              <div className="bg-zinc-900/50 rounded-lg px-3 py-2.5" data-testid="drawer-totals">
                <p className="text-[9px] text-zinc-600 uppercase mb-2">Totals</p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  <MiniPill label="Paid" value={selectedRun.totals?.affiliates_paid || 0} color="emerald" />
                  <MiniPill label="Below Threshold" value={selectedRun.totals?.skipped_below_threshold || 0} color="amber" />
                  <MiniPill label="Not Connected" value={selectedRun.totals?.skipped_not_connected || 0} color="zinc" />
                  <MiniPill label="Errors" value={selectedRun.totals?.errors || 0} color="red" />
                </div>
              </div>

              {/* Config */}
              {selectedRun.config && (
                <div className="bg-zinc-900/50 rounded-lg px-3 py-2.5" data-testid="drawer-config">
                  <p className="text-[9px] text-zinc-600 uppercase mb-1.5">Config</p>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-[10px]">
                    <span className="text-zinc-400">Threshold: <span className="text-zinc-200">${selectedRun.config.threshold_usd}</span></span>
                    <span className="text-zinc-400">Hold: <span className="text-zinc-200">{selectedRun.config.hold_days}d</span></span>
                    <span className="text-zinc-400">Rate: <span className="text-zinc-200">{(selectedRun.config.commission_rate * 100).toFixed(0)}%</span></span>
                    <span className="text-zinc-400">Day: <span className="text-zinc-200">{selectedRun.config.payout_day}st</span></span>
                  </div>
                </div>
              )}

              {/* Per-Affiliate Details */}
              {selectedRun.affiliate_details && selectedRun.affiliate_details.length > 0 ? (
                <div data-testid="drawer-affiliate-details">
                  <p className="text-[9px] text-zinc-600 uppercase mb-2">Affiliate Breakdown ({selectedRun.affiliate_details.length})</p>
                  <div className="space-y-2">
                    {selectedRun.affiliate_details.map((d, i) => (
                      <div key={i} className="bg-zinc-900/60 border border-zinc-800/40 rounded-lg px-3 py-2.5" data-testid={`affiliate-detail-${i}`}>
                        <div className="flex items-center justify-between mb-1.5">
                          <span className="text-[11px] text-zinc-300 truncate max-w-[60%]">{d.payout_email || d.user_id?.slice(0, 12)}</span>
                          <span className={`text-[10px] font-medium ${actionColor(d.action)}`}>{actionLabel(d.action)}</span>
                        </div>
                        {(d.pending_cents !== undefined) && (
                          <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[10px] mb-1">
                            <span className="text-amber-400/70">Pending: ${(d.pending_cents / 100).toFixed(2)}</span>
                            <span className="text-emerald-400/70">Available: ${(d.available_cents / 100).toFixed(2)}</span>
                            <span className="text-zinc-500">Paid: ${(d.paid_cents / 100).toFixed(2)}</span>
                            <span className="text-zinc-200 font-medium">Net: ${(d.net_available_cents / 100).toFixed(2)}</span>
                          </div>
                        )}
                        {d.payout_amount_cents && (
                          <p className="text-[10px] text-emerald-400">Payout: ${(d.payout_amount_cents / 100).toFixed(2)}</p>
                        )}
                        {d.transfer_id && (
                          <p className="text-[10px] text-zinc-500 truncate">Transfer: {d.transfer_id}</p>
                        )}
                        {d.failure_reason && (
                          <p className="text-[10px] text-red-400 break-words">Error: {d.failure_reason}</p>
                        )}
                        {d.connect_status && d.connect_status !== 'complete' && (
                          <p className="text-[10px] text-zinc-600">Connect: {d.connect_status}</p>
                        )}
                        {d.contributing_entries_count > 0 && (
                          <p className="text-[10px] text-zinc-600">{d.contributing_entries_count} ledger entries</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <p className="text-[11px] text-zinc-600 text-center py-4">No affiliate details in this run</p>
              )}

              {/* Debug Log */}
              {selectedRun.debug_log && (
                <div data-testid="drawer-debug-log">
                  <p className="text-[9px] text-zinc-600 uppercase mb-1.5">Debug Log</p>
                  <pre className="text-[10px] text-zinc-500 bg-zinc-900/80 rounded-lg px-3 py-2 overflow-x-auto whitespace-pre-wrap break-words max-h-40 overflow-y-auto font-mono">
                    {typeof selectedRun.debug_log === 'string' ? selectedRun.debug_log : selectedRun.debug_log.join('\n')}
                  </pre>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Main Panel */}
      <div className="bg-zinc-900/50 border border-zinc-800/40 rounded-xl p-4" data-testid="affiliate-payouts-admin">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <DollarSign className="w-4 h-4 text-zinc-500" />
            <h2 className="text-xs font-semibold text-zinc-300 uppercase tracking-wider">Affiliate Payouts</h2>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setConfirmAction('dry')}
              disabled={runningDryRun}
              className="text-[10px] px-2.5 py-1 bg-blue-600/15 text-blue-400 rounded-md hover:bg-blue-600/25 disabled:opacity-50 transition-colors flex items-center gap-1"
              data-testid="run-dry-run-btn"
            >
              {runningDryRun ? <Loader2 className="w-3 h-3 animate-spin" /> : <Activity className="w-3 h-3" />}
              {runningDryRun ? 'Running...' : 'Dry-Run'}
            </button>
            <button
              onClick={() => setConfirmAction('real')}
              disabled={runningPayout}
              className="text-[10px] px-2.5 py-1 bg-emerald-600/15 text-emerald-400 rounded-md hover:bg-emerald-600/25 disabled:opacity-50 transition-colors flex items-center gap-1"
              data-testid="run-real-payout-btn"
            >
              {runningPayout ? <Loader2 className="w-3 h-3 animate-spin" /> : <DollarSign className="w-3 h-3" />}
              {runningPayout ? 'Running...' : 'Real Payout'}
            </button>
          </div>
        </div>

        {/* Affiliates Summary */}
        <div className="space-y-1.5 mb-4 max-h-[160px] overflow-y-auto">
          <p className="text-[10px] text-zinc-600">{affiliates.length} affiliate{affiliates.length !== 1 ? 's' : ''}</p>
          {affiliates.map(a => (
            <div key={a.id} className="flex items-center gap-2 text-[10px] bg-zinc-900/60 rounded-md px-2.5 py-1.5">
              <div className={`w-2 h-2 rounded-full shrink-0 ${a.connect_onboarding_status === 'complete' ? 'bg-emerald-400' : a.status === 'suspended' ? 'bg-red-400' : 'bg-amber-400'}`} />
              <div className="flex-1 min-w-0">
                <p className="text-zinc-300 truncate">{a.payout_email || a.code}</p>
                <div className="flex flex-wrap gap-x-2 gap-y-0.5 text-zinc-600">
                  <span>{a.total_referrals} refs</span>
                  <span>Avail: ${((a.available_cents || 0) / 100).toFixed(2)}</span>
                  <span>Paid: ${((a.paid_cents || 0) / 100).toFixed(2)}</span>
                  <span className={a.connect_onboarding_status === 'complete' ? 'text-emerald-500' : 'text-amber-500'}>{a.connect_onboarding_status}</span>
                  {a.status === 'suspended' && <span className="text-red-400">SUSPENDED</span>}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Payout Runs Table */}
        <div className="border-t border-zinc-800/50 pt-3" data-testid="payout-runs-table">
          <p className="text-[10px] text-zinc-600 mb-2">Payout Runs ({payoutRuns.length})</p>
          {payoutRuns.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-[10px]">
                <thead>
                  <tr className="text-zinc-600 text-left border-b border-zinc-800/50">
                    <th className="pb-1.5 pr-2 font-medium">Date</th>
                    <th className="pb-1.5 pr-2 font-medium">Status</th>
                    <th className="pb-1.5 pr-2 font-medium text-right">Affiliates</th>
                    <th className="pb-1.5 pr-2 font-medium text-right">Paid</th>
                    <th className="pb-1.5 pr-2 font-medium text-right hidden sm:table-cell">Skipped</th>
                    <th className="pb-1.5 pr-2 font-medium text-right hidden sm:table-cell">Errors</th>
                    <th className="pb-1.5 font-medium text-right">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {payoutRuns.slice(0, 20).map(r => {
                    const t = r.totals || {};
                    const totalAffs = (t.affiliates_paid || 0) + (t.skipped_below_threshold || 0) + (t.skipped_not_connected || 0) + (t.errors || 0);
                    return (
                      <tr
                        key={r.id}
                        onClick={() => openRunDetail(r)}
                        className="border-b border-zinc-800/30 hover:bg-zinc-800/30 cursor-pointer transition-colors"
                        data-testid={`payout-run-row-${r.id?.slice(0, 8)}`}
                      >
                        <td className="py-1.5 pr-2 text-zinc-400 whitespace-nowrap">{r.run_at ? new Date(r.run_at).toLocaleDateString() + ' ' + new Date(r.run_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '—'}</td>
                        <td className="py-1.5 pr-2"><span className={`px-1.5 py-0.5 rounded text-[9px] font-medium ${statusColor(r.status)}`}>{r.status?.replace(/_/g, ' ')}</span></td>
                        <td className="py-1.5 pr-2 text-right text-zinc-300 tabular-nums">{totalAffs}</td>
                        <td className="py-1.5 pr-2 text-right text-emerald-400 tabular-nums">{t.affiliates_paid || 0}</td>
                        <td className="py-1.5 pr-2 text-right text-zinc-500 tabular-nums hidden sm:table-cell">{(t.skipped_below_threshold || 0) + (t.skipped_not_connected || 0)}</td>
                        <td className="py-1.5 pr-2 text-right tabular-nums hidden sm:table-cell"><span className={t.errors > 0 ? 'text-red-400' : 'text-zinc-600'}>{t.errors || 0}</span></td>
                        <td className="py-1.5 text-right text-zinc-200 font-medium tabular-nums">${((t.total_cents || 0) / 100).toFixed(2)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-[11px] text-zinc-600 text-center py-4">No payout runs yet</p>
          )}
        </div>
      </div>
    </>
  );
}

function MiniPill({ label, value, color }) {
  const colors = {
    emerald: 'bg-emerald-500/10 text-emerald-400',
    amber: 'bg-amber-500/10 text-amber-400',
    red: 'bg-red-500/10 text-red-400',
    zinc: 'bg-zinc-800/50 text-zinc-400',
    blue: 'bg-blue-500/10 text-blue-400',
  };
  return (
    <div className={`rounded-md px-2 py-1 text-center ${colors[color]}`}>
      <p className="text-sm font-bold tabular-nums">{value}</p>
      <p className="text-[8px] opacity-70">{label}</p>
    </div>
  );
}

function SeverityDot({ type }) {
  const t = type?.toLowerCase() || '';
  const color = t.includes('fail') || t.includes('error') ? 'bg-red-400' : t.includes('warn') ? 'bg-amber-400' : 'bg-zinc-500';
  return <span className={`w-1.5 h-1.5 rounded-full ${color} shrink-0 mt-1`} />;
}

function ActivityIcon({ type }) {
  if (type === 'signup') return <Users className="w-3 h-3 text-emerald-400 shrink-0" />;
  if (type === 'video_generated') return <Video className="w-3 h-3 text-blue-400 shrink-0" />;
  if (type === 'image_generated') return <Image className="w-3 h-3 text-violet-400 shrink-0" />;
  return <Activity className="w-3 h-3 text-zinc-500 shrink-0" />;
}

function timeAgo(iso) {
  try {
    const d = new Date(iso);
    const s = Math.floor((Date.now() - d.getTime()) / 1000);
    if (s < 60) return 'just now';
    if (s < 3600) return `${Math.floor(s / 60)}m ago`;
    if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
    return `${Math.floor(s / 86400)}d ago`;
  } catch { return ''; }
}

function SkeletonDashboard() {
  return (
    <div className="p-6 space-y-5" data-testid="admin-skeleton">
      <div className="h-10 bg-zinc-900/50 rounded-xl animate-pulse w-64" />
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="h-20 bg-zinc-900/50 rounded-xl animate-pulse" />
        ))}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="h-48 bg-zinc-900/50 rounded-xl animate-pulse" />
        ))}
      </div>
    </div>
  );
}
